import { morph } from '@theme/morph';

const PRODUCT_CARD_STITCHING_SELECT = 'product-card:stitching-select';

/** Section that renders a product's swatch items (sections/product-card-stitching-swatches.liquid). */
const SWATCHES_SECTION_ID = 'product-card-stitching-swatches';

/**
 * Product card stitching — reads sibling data from swatch data-* attributes
 * (rendered from custom.product_stitching metafield). No API for image swaps.
 */
class ProductCardStitching extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {HTMLElement | null} */
  #productCard = null;

  /** @type {Promise<boolean> | null} The "+N" swatches request, while in flight. */
  #overflowRequest = null;

  connectedCallback() {
    this.addEventListener('click', this.#handleClick, true);

    const moreButton = this.querySelector('.product-card-stitching__more');
    if (moreButton instanceof HTMLButtonElement) {
      moreButton.addEventListener('click', this.#handleMoreClick);
    }

    this.#productCard = this.closest('product-card') || this.closest('.resource-card');
    this.#productCard?.addEventListener(PRODUCT_CARD_STITCHING_SELECT, this.#handleExternalSelect);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick, true);
    this.#productCard?.removeEventListener(PRODUCT_CARD_STITCHING_SELECT, this.#handleExternalSelect);
    this.#productCard = null;
  }

  /**
   * @param {Event} event
   */
  #handleClick = (event) => {
    const button = event.target instanceof Element ? event.target.closest('.product-card-stitching__swatch') : null;
    if (!(button instanceof HTMLButtonElement) || !this.contains(button)) return;

    event.preventDefault();
    event.stopPropagation();

    if (button.classList.contains('product-card-stitching__swatch--active')) return;

    this.#selectSwatch(button);
  };

  /**
   * Sync card from quick-add drawer colour change.
   * @param {Event} event
   */
  #handleExternalSelect = async (event) => {
    const productId = event.detail?.productId;
    if (!productId) return;

    let button = this.querySelector(`.product-card-stitching__swatch[data-product-id="${productId}"]`);
    // The colour may be one of the swatches behind "+N" that haven't been loaded yet.
    if (!button && this.hasAttribute('data-overflow-pending')) {
      await this.#loadOverflowSwatches();
      button = this.querySelector(`.product-card-stitching__swatch[data-product-id="${productId}"]`);
    }
    if (!(button instanceof HTMLButtonElement)) return;
    if (button.classList.contains('product-card-stitching__swatch--active')) return;

    this.#selectSwatch(button);
  };

  /**
   * @param {Event} event
   */
  #handleMoreClick = async (event) => {
    event.preventDefault();
    event.stopPropagation();

    // Repeat clicks while the swatches are still loading are ignored.
    if (this.#overflowRequest) return;

    if (this.hasAttribute('data-overflow-pending')) {
      const loaded = await this.#loadOverflowSwatches();
      if (!loaded) return;
    }

    const expanded = this.dataset.expanded === 'true';
    if (expanded) {
      this.removeAttribute('data-expanded');
    } else {
      this.setAttribute('data-expanded', 'true');
    }

    const moreButton = this.querySelector('.product-card-stitching__more');
    if (moreButton instanceof HTMLButtonElement) {
      moreButton.setAttribute('aria-expanded', expanded ? 'false' : 'true');
    }
  };

  /**
   * Fetches the swatches behind "+N" (not rendered with the card - see
   * snippets/product-card-stitching.liquid) and adds them to this card's list, once.
   *
   * Only swatches the card doesn't already have are added (matched by position), they keep the
   * card's own swatch size, and the one for the colour the card currently shows is marked active.
   *
   * @returns {Promise<boolean>} Whether the swatches are now in place.
   */
  #loadOverflowSwatches() {
    if (this.#overflowRequest) return this.#overflowRequest;

    const moreButton = this.querySelector('.product-card-stitching__more');
    const productUrl = this.dataset.productUrl;
    if (!productUrl) return Promise.resolve(false);

    const url = new URL(productUrl, window.location.origin);
    url.search = '';
    url.hash = '';
    url.searchParams.set('section_id', SWATCHES_SECTION_ID);

    moreButton?.setAttribute('aria-busy', 'true');
    this.removeAttribute('data-overflow-error');

    this.#overflowRequest = fetch(url.toString())
      .then(async (response) => {
        if (!response.ok) throw new Error(`Swatches request failed (${response.status})`);

        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        const items = html.querySelectorAll('[data-stitching-swatches] > .product-card-stitching__item');
        const list = this.querySelector('.product-card-stitching__list');
        if (!list || items.length === 0) throw new Error('Swatches missing from response');

        const existingSwatches = list.querySelectorAll('.product-card-stitching__swatch');
        const existingIndexes = new Set(
          Array.from(existingSwatches, (swatch) => /** @type {HTMLElement} */ (swatch).dataset.siblingIndex)
        );
        const swatchSize = /** @type {HTMLElement | undefined} */ (existingSwatches[0])?.style.getPropertyValue(
          '--swatch-size'
        );
        const activeProductId = (this.closest('product-card') || this.closest('.resource-card'))?.dataset.productId;

        const fragment = document.createDocumentFragment();
        for (const item of items) {
          const swatch = item.querySelector('.product-card-stitching__swatch');
          if (!(swatch instanceof HTMLElement) || existingIndexes.has(swatch.dataset.siblingIndex)) continue;

          item.classList.add('product-card-stitching__item--overflow');
          item.setAttribute('data-overflow', '');
          if (swatchSize) swatch.style.setProperty('--swatch-size', swatchSize);

          if (activeProductId) {
            const isActive = swatch.dataset.productId === activeProductId;
            swatch.classList.toggle('product-card-stitching__swatch--active', isActive);
            swatch.setAttribute('aria-current', isActive ? 'true' : 'false');
          }

          fragment.append(document.importNode(item, true));
        }

        list.append(fragment);
        this.removeAttribute('data-overflow-pending');
        return true;
      })
      .catch((error) => {
        console.error(error);
        // "+N" stays as it was, so the shopper can try again.
        this.setAttribute('data-overflow-error', '');
        return false;
      })
      .finally(() => {
        moreButton?.removeAttribute('aria-busy');
        this.#overflowRequest = null;
      });

    return this.#overflowRequest;
  }

  /**
   * @param {HTMLButtonElement} button
   */
  #selectSwatch(button) {
    const productCard = this.closest('product-card') || this.closest('.resource-card');
    if (!(productCard instanceof HTMLElement)) return;

    this.querySelectorAll('.product-card-stitching__swatch').forEach((swatch) => {
      if (!(swatch instanceof HTMLButtonElement)) return;
      const isActive = swatch === button;
      swatch.classList.toggle('product-card-stitching__swatch--active', isActive);
      swatch.setAttribute('aria-current', isActive ? 'true' : 'false');
    });

    // A colour without an image keeps the card's current images; everything else (title, price,
    // reviews, stock, links, quick add) still switches to it below.
    const primarySrc = button.dataset.primarySrc || '';
    const primarySrcset = button.dataset.primarySrcset || '';
    const secondarySrc = button.dataset.secondarySrc || primarySrc;
    const secondarySrcset = button.dataset.secondarySrcset || primarySrcset;
    const alt = button.getAttribute('aria-label') || '';

    if (primarySrc) {
      this.#updateGalleryImages(productCard, {
        primarySrc,
        primarySrcset,
        secondarySrc,
        secondarySrcset,
        alt,
      });
    }

    const productUrl = button.dataset.productUrl;
    if (productUrl) {
      productCard.querySelectorAll('a.product-card__link, a.resource-card__link, a[ref="productCardLink"], a[ref="cardGalleryLink"], a[ref="productTitleLink"]').forEach((link) => {
        if (link instanceof HTMLAnchorElement) {
          link.href = productUrl;
        }
      });
    }

    if (button.dataset.productId) {
      productCard.dataset.productId = button.dataset.productId;
    }

    if (button.dataset.variantId) {
      this.#updateWishlistVariant(productCard, button.dataset.variantId);
    }

    if (primarySrc && productCard.hasAttribute('data-featured-media-url')) {
      productCard.setAttribute('data-featured-media-url', primarySrc);
    }

    this.#updateTitleAndPrice(productCard, button);
    this.#updateRating(productCard, button);
    this.#updateBadges(productCard, button);
    this.#updateQuickAddForm(productCard, button);

    if (typeof productCard.updateStitchedSibling === 'function') {
      productCard.updateStitchedSibling({
        id: Number(button.dataset.productId),
        url: productUrl,
        title: button.dataset.productTitle,
        primaryImage: primarySrc ? { src: primarySrc, srcset: primarySrcset, alt } : undefined,
        secondaryImage: primarySrc ? { src: secondarySrc, srcset: secondarySrcset, alt } : undefined,
      });
    }

    if (productCard.dataset.cardMode === 'b2b' && button.dataset.productHandle) {
      this.#fetchB2bVariants(productCard, button.dataset.productHandle);
    }
  }

  /**
   * @param {HTMLElement} productCard
   * @param {HTMLButtonElement} button
   */
  #updateTitleAndPrice(productCard, button) {
    const title = button.dataset.productTitle;
    if (title) {
      const heading = productCard.querySelector('[ref="productTitleLink"] [role="heading"], .resource-card__title');
      if (heading) heading.textContent = title;

      const quickAdd = productCard.querySelector('quick-add-component');
      if (quickAdd) quickAdd.setAttribute('data-product-title', title);
    }

    if (productCard.dataset.cardMode === 'b2b') return;

    // Only the price container is swapped, for the colour's own one. The template is the colour's
    // whole price snippet output; putting all of it inside the card's container used to nest a
    // second container (and, before the template stopped including it, a second review widget).
    const priceTemplate = this.#getSwatchTemplate(button, 'data-stitching-price');
    const newPrice = priceTemplate?.content.querySelector('[ref="priceContainer"]');
    const priceContainer = productCard.querySelector('product-price [ref="priceContainer"], [ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(document.importNode(newPrice, true));
    }

    const productPrice = productCard.querySelector('product-price');
    if (productPrice instanceof HTMLElement && button.dataset.productId) {
      productPrice.setAttribute('data-product-id', button.dataset.productId);
    }
  }

  /**
   * One of the swatch's `<template data-stitching-*>` copies (snippets/product-card-stitching-swatch.liquid).
   * @param {HTMLButtonElement} button
   * @param {string} attribute
   * @returns {HTMLTemplateElement | null}
   */
  #getSwatchTemplate(button, attribute) {
    const template = button.closest('.product-card-stitching__item')?.querySelector(`template[${attribute}]`);
    return template instanceof HTMLTemplateElement ? template : null;
  }

  /**
   * Points the card's review widget at the picked colour.
   *
   * The widget is swapped for a new, empty one with the colour's `data-sku`, in one step - the old
   * one leaves the page as the new one arrives, so the card never shows two. The theme's Reviews.io
   * setup (layout/theme.liquid) renders any new widget that appears; Reviews.io skips widgets it has
   * already rendered, so nothing is rendered twice. A card without a widget doesn't get one.
   *
   * @param {HTMLElement} productCard
   * @param {HTMLButtonElement} button
   */
  #updateRating(productCard, button) {
    const sku = button.dataset.ratingSku;
    if (!sku) return;

    productCard.querySelectorAll('.ruk_rating_snippet').forEach((widget) => {
      if (!(widget instanceof HTMLElement) || widget.dataset.sku === sku) return;
      // Only this card's own widget, not one in a card nested inside it.
      if (widget.closest('product-card, .resource-card') !== productCard) return;

      // A new element, not a reset of this one: Reviews.io marks the widgets it renders with its own
      // attributes (`data-done`, `aria-label`, `style`...) that would stop it rendering this one again.
      const replacement = document.createElement(widget.localName);
      replacement.className = widget.className;
      replacement.dataset.sku = sku;
      widget.replaceWith(replacement);
    });
  }

  /**
   * Shows the picked colour's "Sold out" / "Sale" / "New" badge on the card's image, or none.
   * @param {HTMLElement} productCard
   * @param {HTMLButtonElement} button
   */
  #updateBadges(productCard, button) {
    const template = this.#getSwatchTemplate(button, 'data-stitching-badges');
    const gallery = productCard.querySelector('.card-gallery');
    if (!template || !gallery) return;

    const newBadges = template.content.querySelector('.product-badges');
    const badges = gallery.querySelector(':scope > .product-badges');

    if (badges && newBadges) {
      badges.replaceWith(document.importNode(newBadges, true));
    } else if (badges) {
      badges.remove();
    } else if (newBadges) {
      // Where the card gallery renders it: after the images, before quick add.
      gallery.insertBefore(document.importNode(newBadges, true), gallery.querySelector(':scope > quick-add-component'));
    }
  }

  /**
   * @param {HTMLElement} productCard
   * @param {HTMLButtonElement} button
   */
  #updateQuickAddForm(productCard, button) {
    const quickAdd = productCard.querySelector('quick-add-component');
    if (!quickAdd) return;

    const available = !button.classList.contains('product-card-stitching__swatch--unavailable');
    this.#updateQuickAddVisibility(productCard, available);

    if (button.dataset.productUrl) {
      quickAdd.setAttribute('data-product-url', button.dataset.productUrl);
    }

    const formComponent = quickAdd.querySelector('product-form-component');
    if (formComponent instanceof HTMLElement && button.dataset.productId) {
      formComponent.setAttribute('data-product-id', button.dataset.productId);
    }

    const variantInput = quickAdd.querySelector('input[ref="variantId"]');
    if (variantInput instanceof HTMLInputElement && button.dataset.variantId) {
      variantInput.value = button.dataset.variantId;
      variantInput.disabled = !available;
    }
  }

  /**
   * Shows quick add for a colour in stock and hides it for a sold-out one - the same rule, from the
   * same settings, as the card's server-rendered `--quick-add-*` properties
   * (snippets/product-card.liquid, snippets/nosto-product-card-content.liquid).
   * @param {HTMLElement} productCard
   * @param {boolean} available
   */
  #updateQuickAddVisibility(productCard, available) {
    // B2B cards never show quick add.
    if (productCard.dataset.cardMode === 'b2b') return;
    // Markup rendered before the settings were exposed on this element: leave it as rendered.
    if (this.dataset.quickAdd === undefined) return;

    const content = productCard.querySelector('.product-card__content');
    if (!(content instanceof HTMLElement)) return;

    const quickAddSetting = this.dataset.quickAdd === 'true';
    const mobileQuickAddSetting = this.dataset.mobileQuickAdd === 'true';
    const desktop = available && quickAddSetting;
    const mobile = available && (quickAddSetting || mobileQuickAddSetting);

    content.style.setProperty('--quick-add-display', desktop ? 'flex' : 'none');
    content.style.setProperty('--quick-add-mobile-display', mobile ? 'flex' : 'none');
    content.style.setProperty('--quick-add-mobile-opacity', mobile ? '1' : '0');
  }

  /**
   * @param {HTMLElement} productCard
   * @param {string} variantId
   */
  #updateWishlistVariant(productCard, variantId) {
    const wishlistButton = productCard.querySelector('.card-gallery wishlist-button');
    if (wishlistButton instanceof HTMLElement) {
      wishlistButton.setAttribute('data-variant-id', variantId);
    }
  }

  /**
   * @param {HTMLElement} productCard
   * @param {{ primarySrc: string, primarySrcset: string, secondarySrc: string, secondarySrcset: string, alt: string }} images
   */
  #updateGalleryImages(productCard, images) {
    const gallery = productCard.querySelector('.card-gallery') || productCard.querySelector('.resource-card__media');
    if (!gallery) return;

    const slideImages = gallery.querySelectorAll('slideshow-slide:not([hidden]) img');
    const targets = slideImages.length > 0 ? slideImages : gallery.querySelectorAll('img.product-media__image, img');

    if (targets[0] instanceof HTMLImageElement) {
      this.#applyImage(targets[0], images.primarySrc, images.primarySrcset, images.alt);
    }

    if (targets[1] instanceof HTMLImageElement) {
      this.#applyImage(targets[1], images.secondarySrc, images.secondarySrcset, images.alt);
    }

    const slideshow = gallery.querySelector('slideshow-component');
    if (slideshow && 'select' in slideshow && typeof slideshow.select === 'function') {
      /** @type {import('slideshow').Slideshow} */ (slideshow).select(0, undefined, { animate: false });
    }
  }

  /**
   * @param {HTMLImageElement} img
   * @param {string} src
   * @param {string} srcset
   * @param {string} [alt]
   */
  #applyImage(img, src, srcset, alt) {
    if (srcset) {
      img.srcset = srcset;
    } else {
      img.removeAttribute('srcset');
    }

    img.src = src;

    if (alt) {
      img.alt = alt;
    }

    img.setAttribute('data-max-resolution', src.replace(/width=\d+/, 'width=3840'));
  }

  /**
   * B2B only — refresh size picker from section rendering API.
   * @param {HTMLElement} productCard
   * @param {string} handle
   */
  async #fetchB2bVariants(productCard, handle) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const url = new URL(`/products/${handle}`, window.location.origin);
    url.searchParams.set('section_id', 'section-rendering-stitched-card');

    try {
      const response = await fetch(url.toString(), { signal: this.#abortController.signal });
      if (!response.ok) return;

      const html = new DOMParser().parseFromString(await response.text(), 'text/html');

      this.#morph(productCard, html, 'variant-picker');
      this.#morph(productCard, html, 'product-price', '.product-card__content product-price');
      this.#morph(productCard, html, 'product-sku-component');
      this.#morph(productCard, html, 'product-form-component', '.buy-buttons-block product-form-component');
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') return;
      console.error(error);
    }
  }

  /**
   * @param {HTMLElement} productCard
   * @param {Document} html
   * @param {string} selector
   * @param {string} [cardSelector]
   */
  #morph(productCard, html, selector, cardSelector) {
    const current = productCard.querySelector(cardSelector || selector);
    const updated = html.querySelector(selector);
    if (current && updated) morph(current, updated);
  }
}

if (!customElements.get('product-card-stitching')) {
  customElements.define('product-card-stitching', ProductCardStitching);
}

export default ProductCardStitching;
